import React, { useEffect, useMemo, useState } from 'react'
import { useTable, usePagination, useSortBy, Column } from 'react-table'
import useApi from '../../../../../../hooks/useQuery'
import { GET_FILTERED_APPOINTMENTS } from '../../../GraphQLQueries/appointment'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { ToastMessageContainer } from '../../../../../../_ePulse/layout/components/ToastMessageContainer'
import { LoadingSpinner } from '../../../../../../_ePulse/layout/components/LoadingSpinner'
import { toAbsoluteUrl } from '../../../../../../_ePulse/helpers'
import { useGetPageLimit } from '../../../../../../data/config'
import '../../../../../../App.css'

interface AppointmentData {
  id: string
  patientName: string
  patientMobileNumber: string
  appointmentStartTime: string
  latestStatus: string
  testProductType: string
  scanTestDetails: Array<{ testGroupName: string; testName: string }>
}

const getColor = (statusTag: string) => {
  if (statusTag == 'Confirmed' || statusTag === 'Requested') return '#257FD4'
  else if (statusTag === 'Pending') return 'rgba(175, 29, 29, 1)'
  else if (statusTag === 'Rescheduled') return '#F49404'
  else if (statusTag === 'Cancelled By Patient') return '#F82C31'
  else if (statusTag === 'Cancelled By Center') return '#F82C31'
  else if (statusTag === 'No Show') return '#C1BA04'
  else if (statusTag === 'Waiting For Results') return '#15C032'
  else if (statusTag === 'Results Ready') return '#15C032'
  else if (statusTag === 'Closed') return '#393939'
  else if (statusTag === 'PartiallyPaid') return '#257FD4'
  else if (statusTag === 'Paid') return '#15C032'
  else if (statusTag === 'Refund') return '#C1BA04'
  else if (statusTag === 'PartialRefund') return '#C1BA04'
  else if (statusTag === 'PENDING') return '#257FD4'
}

const Report: React.FC = () => {
  const navigate = useNavigate()

  const [selectedFilter, setSelectedFilter] = useState<any>('All Reports')
  const [reportList, setReportList] = useState<any>([])
  const [pageNumber, setpageNumber] = useState(1)
  const [limit, setlimit] = useState(useGetPageLimit())
  const [totalPages, setTotalPages] = useState(0)

  const [searchField, setSearchField] = useState('')      // 'patientName' | 'mobileNumber'
  const [searchValue, setSearchValue] = useState('')       // 'ct_scan' | 'lab_test' | ''
  const [filteredList, setFilteredList] = useState<any>([])

  const { loading, error, data, refetch } = useApi('', GET_FILTERED_APPOINTMENTS, {
    listFilter: { page: pageNumber, limit: limit },
    appointmentStatus: selectedFilter,
  })

  useEffect(() => {
    if (data) {
      setReportList(data?.getDiagnosisAppointmentByStatus?.data)
      setTotalPages(data?.getDiagnosisAppointmentByStatus?.totalPages || 0)
      setFilteredList(data?.getDiagnosisAppointmentByStatus?.data || [])
    }
  }, [data])

  useEffect(() => {
    if(!(searchField === 'patientName' || searchField === 'mobileNumber')) {
 refetch({ listFilter: { page: 1, limit }, appointmentStatus: selectedFilter, searchQuery: { field: searchField, value: searchValue } })
    }
     
  }, [searchValue])

  // ── Search handlers ────────────────────────────────
  const handleKeyPress = (event: any) => {
    if (event.key === 'Enter') triggerSearch()
  }

  const triggerSearch = () => {
    refetch({ listFilter: { page: 1, limit }, appointmentStatus: selectedFilter, searchQuery: { field: searchField, value: searchValue } })
    setpageNumber(1)
  }

  const clearSearch = () => {
    setSearchField('')
    setSearchValue('')
    setSelectedFilter('All Reports')
    refetch({ listFilter: { page: 1, limit }, appointmentStatus: selectedFilter })
    setpageNumber(1)
  }

  const columns = useMemo<Column<AppointmentData>[]>(
    () => [
      {
        Header: 'Patient Name',
        accessor: 'patientName' as const,
        style: {
          paddingLeft: '3rem',
          paddingRight: '1rem',
          textAlign: 'center',
        },
        Cell: ({ value }: any) => <span>{value}</span>,
      },
      {
        Header: 'Mobile No.',
        accessor: 'patientMobileNumber' as const,
        Cell: ({ value }: any) => <span>{value || '—'}</span>,
      },
      {
        Header: 'Appointment Time',
        accessor: 'appointmentStartTime' as const,
        Cell: ({ value }: any) => (
          <span style={{ color: '#fff' }}>{value ? new Date(value).toLocaleString() : ''}</span>
        ),
      },
      {
        Header: 'Status',
        accessor: 'latestStatus' as const,
        Cell: ({ value }: any) => (
          <span className='badge text-epulse-white' style={{ backgroundColor: getColor(value) }}>
            {value}
          </span>
        ),
      },
      {
        Header: 'Product',
        accessor: 'testProductType' as const,
        Cell: ({ value }: any) => {
          const productType = value
          const formattedProductType =
            productType === 'ct_scan'
              ? 'CT Scan'
              : productType === 'lab_test'
                ? 'Lab Tests'
                : productType

          return <span>{formattedProductType}</span>
        },
      },
      {
        Header: 'Tests',
        accessor: 'scanTestDetails' as const,
        Cell: ({ value }: any) => {
          const groupMap: { [groupName: string]: string[] } = {}
          value?.forEach((test: any) => {
            if (!groupMap[test.testGroupName]) groupMap[test.testGroupName] = []
            groupMap[test.testGroupName].push(test.testName)
          })
          return (
            <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
              {Object.entries(groupMap).map(([groupName, testNames]) => (
                <li key={groupName}>
                  <span className='fw-semibold'>{groupName}:</span>{' '}
                  <span>{testNames.join(', ')}</span>
                </li>
              ))}
            </ul>
          )
        },
      },
    ],
    []
  )

  const tableInstance = useTable<AppointmentData>(
    {
      columns,
      data: filteredList,
    },
    useSortBy,
    usePagination
  )

  const { getTableProps, getTableBodyProps, headerGroups, headers, rows, prepareRow } = tableInstance

  return (
    <div>
      <ToastMessageContainer />
      <LoadingSpinner isLoading={loading} />

      <div className='d-flex justify-content-between py-5 px-3'>
        <h2>Report</h2>
      </div>

      {/* ── Filter Bar (PatientListTable pattern) ── */}
      <div className='filter-row ePulse-divider'>

          {/* 3. Search By (Name / Mobile) */}
        <div className='filter-head'>Search By</div>
        <select
          className='form-control'
          value={searchField}
          onChange={(e: any) => {
            setSearchField(e.target.value)
            if (e.target.value === '') {
              setSearchValue('')
            }
          }}
        >
          <option value=''>All</option>
          <option value='patientName'>Patient Name</option>
          <option value='mobileNumber'>Mobile Number</option>
          <option value='status'>Status</option>
          <option value='productType'>Product Type</option>
          <option value='appointmentDate'>Appointment Date</option>

        </select>

        {searchField !== '' && (searchField==='patientName' || searchField==='mobileNumber') && (
          <>
            <div className='searchbox-wrapper'>
              <input
                type='text'
                data-kt-user-table-filter='search'
                className='form-control'
                placeholder='Start typing to search'
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              <span className='serachbtn-wrapper' onClick={triggerSearch}>
                <img
                  src={toAbsoluteUrl('/media/ePulse/icons/search_icon.svg')}
                  alt='search'
                  className='h-45px'
                />
              </span>
            </div>
            <span className='filter-action' onClick={clearSearch}>
              Clear
            </span>
          </>
        )}

        {/* 1. Status */}
     {searchField === 'status' &&
     <> <div className='filter-head'>Status</div>
      <select
        className='form-control'
        value={selectedFilter}
        onChange={(e: any) => {
          setReportList([])
          setSearchValue(e.target.value)
          setSelectedFilter(e.target.value)
        }}
      >
        <option value='All Reports'>All Reports</option>
        <option value='Waiting For Results'>Waiting For Results</option>
        <option value='Results Ready'>Results Ready</option>
        <option value='Closed'>Closed</option>
      </select>
       <span className='filter-action' onClick={clearSearch}>
            Clear
          </span>
      </>}

        {/* 2. Product Type */}
      { searchField === 'productType' && <>
        <div className='filter-head'>Product Type</div>
        <select
          className='form-control'
          value={searchValue}
          onChange={(e: any) => setSearchValue(e.target.value)}
        >
          <option value=''>All</option>
          <option value='ct_scan'>CT Scan</option>
          <option value='lab_test'>Lab Tests</option>
          <option value='mri'>MRI</option>
          <option value='x_ray'>X-Ray</option>
          <option value='ultra_sound'>Ultrasound</option>
        </select>
         <span className='filter-action' onClick={clearSearch}>
            Clear
          </span>
        </>}

      

        {/* 3. Appointment Date */}
   {  searchField === 'appointmentDate' && <>
        <div className='filter-head'>Appt. Date</div>
        <input
          type='date'
          className='form-control'
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        
          <span className='filter-action' onClick={clearSearch}>
            Clear
          </span>
      
        </>}

      </div>

      <div className='admin-table'>
        <table {...getTableProps()} className='table'>
          <thead>
            <tr className='text-center text-dark fw-bolder fs-6 MyReactTableClass'>
              {headers.map((column: any, index: number) => {
                return (
                  <th
                    key={column.id}
                    style={{
                      paddingLeft: '1rem',
                      //   marginRight: '1rem',
                    }}
                  >
                    {column.render('Header')}
                  </th>
                )
              })}
            </tr>
          </thead>
          {/* <thead>
          {headerGroups.map((headerGroup: any) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              className="text-center text-dark fw-bolder fs-6 MyReactTableClass"
            >
              {headerGroup.headers.map((column: any) => (
                <th
                  {...column.getHeaderProps(
                    column.getSortByToggleProps?.()
                  )}
                >
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead> */}

          <tbody {...getTableBodyProps()} className='text-black fw-normal text-center'>
            {rows.length > 0
              ? rows.map((row: any, index: number) => {
                prepareRow(row)
                return (
                  <tr
                    {...row.getRowProps()}
                    className='bg-white cursor-pointer hover-row'
                    onClick={() =>
                      navigate('/scancenter/report-flow', {
                        state: {
                          appointmentId: row.original.id,
                          testProductType: row.original.testProductType,
                          latestStatus: row.original.latestStatus,
                          scanTestlength: row.original.scanTestDetails.length,
                        },
                      })
                    }
                  >
                    {row.cells.map((cell: any) => (
                      <td
                        {...cell.getCellProps()}
                        className={clsx({
                          'text-end min-w-100px p-1': cell.column.id === 'actions',
                        })}
                        style={{
                          paddingLeft: '1rem',
                          // marginRight: '1rem',
                        }}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                )
              })
              : !loading && (
                <tr>
                  <td colSpan={columns.length}>
                    <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                      No reports found.
                    </div>
                  </td>
                </tr>
              )}
          </tbody>
        </table>
        {filteredList.length > 0 && (
          <div className='pagination'>
            <div className='page-show'>
              <span>Show Entries</span>
              <select
                className='ms-2 ePulse-pagination-dropdown'
                value={limit}
                onChange={(e) => {
                  setlimit(Number(e.target.value))
                  setpageNumber(1)
                  refetch({ limit: parseInt(e.target.value) })
                }}
              >
                {[10, 20, 30, 40, 50, 100, 500].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className='page-num'>
              <button
                className={
                  pageNumber === 1
                    ? 'ePulse-pagination-button'
                    : clsx('ePulse-pagination-button text-white')
                }
                onClick={() => {
                  setpageNumber(pageNumber - 1)
                  refetch({ page: pageNumber - 1, limit: limit })
                }}
                disabled={pageNumber === 1}
              >
                {/* Previous */}
                <img alt='Previous' src={toAbsoluteUrl('/media/icons/duotune/arrows/arr022.svg')} />
              </button>
              <span className='mx-3'>
                Page{' '}
                <strong>
                  {pageNumber} of {totalPages}
                </strong>{' '}
              </span>
              <button
                className={
                  pageNumber === totalPages
                    ? 'ePulse-pagination-button'
                    : clsx('ePulse-pagination-button text-white')
                }
                onClick={() => {
                  setpageNumber(pageNumber + 1)
                  refetch({ page: pageNumber + 1, limit: limit })
                }}
                disabled={pageNumber === totalPages}
              >
                {/* Next */}
                <img alt='Next' src={toAbsoluteUrl('/media/icons/duotune/arrows/arr023.svg')} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Report
